/**
 * SINGLE SOURCE OF TRUTH for the job form's markup.
 *
 * Creating a job and editing a job are the same form over the same state. They
 * were maintained as two ~3,000 line pages that shared 83% of their lines and
 * had drifted apart in most of what they shared. This is the one copy.
 *
 * The pages keep only what genuinely differs: loading the data, submitting it,
 * and the create-only dialogs passed in as children.
 *
 * Where create and edit differ inside the form itself, that is the `mode` prop
 * or a slot - never a second copy. See JobFormRows' preserveCustomRate.
 */

import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { FormSectionHeader } from './FormSectionHeader';
import { PhoneTitle } from '../PhoneHeader';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { RichTextarea } from '../RichTextarea';
import { Switch } from '../ui/switch';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import {
  Plus,
  Loader2,
  Building2,
  Receipt,
  Calendar,
  Mail,
  Phone,
  UserCog,
} from 'lucide-react';
import { toast } from 'sonner';
import { BriefQuickView } from '../BriefQuickView';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import {
  calculatePackageTotal as packageTotal,
  computeJobFormTotals,
} from '../../lib/jobFinancials';
import { useAuthStore } from '../../store/authStore';
import { useRegion } from '../../hooks/useRegion';
import { cn } from '../../lib/utils';
import { useJobFormHandlers } from './useJobFormHandlers';
import { restorePackagedEquipment } from '../../lib/equipmentOrder';
import { JobEquipmentSection } from './JobEquipmentSection';
import {
  SortablePhaseItem,
  SortableExpenseItem,
  CrewLabourSelect,
  SortableCrewItem,
} from './JobFormRows';
import { BackButton } from '../BackButton';


/**
 * The job form.
 *
 * @param {object}   props
 * @param {string}   props.mode            "create" or "edit"
 * @param {object}   props.formData        form state, owned by the page
 * @param {Function} props.setFormData     its setter
 * @param {Array}    props.clients         client list
 * @param {Array}    props.equipment       equipment inventory
 * @param {Array}    props.equipmentKits   saved kits
 * @param {Array}    props.crewMembers     crew inventory
 * @param {object}   props.defaultRates    the user's default phase rates
 * @param {boolean}  props.loading         a submit is in flight
 * @param {Function} props.onSubmit        form submit handler
 * @param {Function} props.onBack          the back arrow, top left
 * @param {Function} [props.onCancel]      shown under submit when provided
 * @param {object}   [props.linkedBrief]   brief this job was built from
 * @param {Function} [props.onClientSelect] overrides the default client pick
 * @param {node}     [props.clientSelectExtras] extra items in the client Select
 * @param {node}     [props.children]      dialogs the page owns, rendered last
 */
export function JobForm({
  mode,
  formData,
  setFormData,
  clients = [],
  equipment = [],
  equipmentKits = [],
  crewMembers = [],
  defaultRates,
  loading = false,
  onSubmit,
  onBack,
  onCancel,
  linkedBrief = null,
  onClientSelect,
  clientSelectExtras = null,
  children = null,
}) {
  const isCreate = mode === 'create';
  const { formatCurrency, getTaxName, regionConfig } = useRegion();
  const { isAdmin } = useAuthStore();

  const [showPackageDialog, setShowPackageDialog] = useState(false);
  const [editingPackage, setEditingPackage] = useState(null);
  const [packageForm, setPackageForm] = useState({
    name: '',
    equipment_ids: [],
    discount_type: 'percentage',
    discount_value: 0,
  });

  const handlers = useJobFormHandlers({
    formData,
    setFormData,
    crewMembers,
    equipment,
    equipmentKits,
    defaultRates,
  });
  // Everything the equipment section needs is passed to it as `handlers`;
  // these are the ones the rest of the form uses directly.
  const {
    addPhase, updatePhase, removePhase, handlePhaseDragEnd,
    addCrewLabourFromSelect, updateCrewLabour, removeCrewLabour, handleCrewDragEnd,
    addExpense, updateExpense, removeExpense, handleExpenseDragEnd,
    addHiddenCost, updateHiddenCost, removeHiddenCost, handleHiddenCostDragEnd,
  } = handlers;

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const calculatePackageTotal = (pkg) => packageTotal(pkg, formData.equipment);

  const {
    labourTotal,
    crewLabourTotal,
    crewEquipmentTotal,
    crewSuperTotal,
    totalLabour,
    equipmentSubtotal,
    packageDiscountTotal,
    totalEquipment,
    expensesTotal,
    totalExpenses,
    subtotal,
    gstAmount,
    grandTotal,
  } = computeJobFormTotals(formData);

  const handleClientSelect = (clientId) => {
    if (onClientSelect) {
      onClientSelect(clientId);
      return;
    }
    const client = clients.find(c => c.id === clientId);
    if (!client) return;
    setFormData(prev => ({
      ...prev,
      client_id: client.id,
      client: client.business_name,
      contact_email: '',
    }));
  };

  const openPackageDialog = (existingPackage = null) => {
    if (existingPackage) {
      setEditingPackage(existingPackage);
      setPackageForm({
        name: existingPackage.name,
        equipment_ids: existingPackage.equipment_ids,
        discount_type: existingPackage.discount_type,
        discount_value: existingPackage.discount_value || 0,
      });
    } else {
      setEditingPackage(null);
      setPackageForm({
        name: 'Package',
        equipment_ids: [],
        discount_type: 'percentage',
        discount_value: 0,
      });
    }
    setShowPackageDialog(true);
  };

  const handleSavePackage = () => {
    if (packageForm.equipment_ids.length === 0) {
      toast.error('Please select at least one equipment item');
      return;
    }

    if (editingPackage) {
      setFormData(prev => {
        // Items dropped from the package go back to the display order as
        // ordinary equipment, rather than disappearing from the list.
        const removedIds = editingPackage.equipment_ids
          .filter(id => !packageForm.equipment_ids.includes(id));
        const newDisplayOrder = restorePackagedEquipment(
          prev.equipment_display_order, prev.equipment, removedIds, editingPackage.id
        );

        return {
          ...prev,
          equipment_packages: prev.equipment_packages.map(pkg =>
            pkg.id === editingPackage.id
              ? { ...pkg, ...packageForm, discount_value: parseFloat(packageForm.discount_value) || 0 }
              : pkg
          ),
          equipment_display_order: newDisplayOrder,
        };
      });
      toast.success('Package updated');
    } else {
      const newPackageId = crypto.randomUUID();
      setFormData(prev => ({
        ...prev,
        equipment_packages: [
          ...prev.equipment_packages,
          { id: newPackageId, ...packageForm, discount_value: parseFloat(packageForm.discount_value) || 0 },
        ],
        equipment_display_order: [
          ...prev.equipment_display_order,
          { type: 'package', id: newPackageId },
        ],
      }));
      toast.success('Package created');
    }
    setShowPackageDialog(false);
  };

  const handleSubmit = onSubmit;

  return (
    <>
      <div className="space-y-6 animate-in" data-testid={isCreate ? 'job-builder-page' : 'job-edit-page'}>
        {/* Header */}
        <PhoneTitle title={isCreate ? 'New job' : 'Edit job'} onBack={onBack} backLabel={isCreate ? 'Back to Jobs' : 'Back to Job'} />
        <div className="title-row gap-3 max-sm:hidden">
          <BackButton onClick={onBack} label={isCreate ? 'Back to Jobs' : 'Back to Job'} />
          <div className="min-w-0">
            <h1 className="record-title">
              {isCreate ? 'New job' : 'Edit job'}
            </h1>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="grid grid-cols-1 lg:grid-cols-12 gap-6">
          {/* Main Content */}
          <div className="lg:col-span-8 space-y-8">
            {/* Client Selection */}
            <Card>
              <CardHeader className="max-sm:p-4">
                <CardTitle className="flex items-center gap-2">
                  <Building2 className="w-5 h-5" /> Client & project
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4 max-sm:px-4 max-sm:pb-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Select client</Label>
                    <Select
                      value={formData.client_id}
                      onValueChange={handleClientSelect}
                    >
                      <SelectTrigger data-testid="client-select">
                        <SelectValue placeholder="Select a client" />
                      </SelectTrigger>
                      <SelectContent>
                        {clients.map((client) => (
                          <SelectItem key={client.id} value={client.id}>
                            {client.business_name}
                          </SelectItem>
                        ))}
                        {clientSelectExtras}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="project_name">Project Name</Label>
                    <Input
                      id="project_name"
                      value={formData.project_name}
                      onChange={(e) => setFormData(prev => ({ ...prev, project_name: e.target.value }))}
                      placeholder="Project name"
                      required
                      data-testid="job-project-input"
                    />
                  </div>
                </div>

                {/* Show selected client info and contact selection */}
                {formData.client_id && (() => {
                  const selectedClient = clients.find(c => c.id === formData.client_id);
                  if (!selectedClient) return null;
                  const contacts = selectedClient.contacts || [];
                  return (
                    <div className="space-y-3">
                      <div className="p-3 bg-secondary/50 rounded-lg text-sm">
                        <p className="font-medium">{selectedClient.business_name}</p>
                        {selectedClient.billing_address && <p className="text-muted-foreground">{selectedClient.billing_address}</p>}
                      </div>
                      
                      {/* Contact Selection */}
                      {contacts.length > 0 && (
                        <div className="space-y-2">
                          <Label>Select Contact</Label>
                          <Select
                            value={formData.contact_email || "none"}
                            onValueChange={(email) => setFormData(prev => ({ ...prev, contact_email: email === "none" ? "" : email }))}
                          >
                            <SelectTrigger data-testid="contact-select">
                              <SelectValue placeholder="Select a contact..." />
                            </SelectTrigger>
                            <SelectContent align="start">
                              <SelectItem value="none">
                                <span className="text-muted-foreground">None</span>
                              </SelectItem>
                              {contacts.map((contact, idx) => (
                                <SelectItem key={idx} value={contact.email}>
                                  {contact.name || 'Unnamed Contact'}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                      )}
                      
                      {/* Show selected contact details */}
                      {formData.contact_email && (() => {
                        const selectedContact = contacts.find(c => c.email === formData.contact_email);
                        if (!selectedContact) return null;
                        return (
                          <div className="p-3 bg-secondary/50 rounded-lg text-sm space-y-1">
                            {selectedContact.name && <p className="font-medium">{selectedContact.name}</p>}
                            <p className="text-muted-foreground flex items-center gap-1">
                              <Mail className="w-3 h-3" /> {selectedContact.email}
                            </p>
                            {selectedContact.phone && (
                              <p className="text-muted-foreground flex items-center gap-1">
                                <Phone className="w-3 h-3" /> {selectedContact.phone}
                              </p>
                            )}
                          </div>
                        );
                      })()}
                    </div>
                  );
                })()}

                {/* Job Description */}
                <div className="space-y-2">
                  <Label htmlFor="description">Job Description</Label>
                  {linkedBrief && (
                    <BriefQuickView brief={linkedBrief} onOpen={() => window.open(`/briefs/${linkedBrief.id}`, '_blank')} />
                  )}
                  <RichTextarea
                    value={formData.description}
                    onChange={(html) => setFormData(prev => ({ ...prev, description: html }))}
                    placeholder="Brief description of the job scope, deliverables, or special requirements..."
                    rows={6}
                    linkText="Edit description template"
                    linkHref="/settings?tab=estimates"
                  />
                </div>
              </CardContent>
            </Card>

            {/* Phase Rate Cards with Drag & Drop */}
            {!formData.equipment_rental_only && (
            <Card>
              <FormSectionHeader
                icon={Calendar}
                title="Labour phases"
                action={{ label: 'Add Phase', onClick: addPhase, testId: 'add-phase-btn' }}
              />
              <CardContent className="max-sm:px-4 max-sm:pb-4">
                <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handlePhaseDragEnd}>
                  <SortableContext items={formData.phases.map(p => p.id)} strategy={verticalListSortingStrategy}>
                    {formData.phases.map((phase, index) => (
                      <SortablePhaseItem
                        key={phase.id}
                        phase={phase}
                        index={index}
                        onUpdate={updatePhase}
                        onRemove={removePhase}
                        canRemove={true}
                        defaultRates={defaultRates}
                        formatCurrency={formatCurrency}
                        regionConfig={regionConfig}
                        // Editing must not clobber a rate the user has customised;
                        // on a new job there is nothing to preserve.
                        preserveCustomRate={!isCreate}
                      />
                      ))}
                    </SortableContext>
                  </DndContext>
                  <p className="text-xs text-muted-foreground mt-2">Drag items to reorder. Your day rates are saved as defaults.</p>
                </CardContent>
              </Card>
            )}

            {/* Crew Labour Section */}
            {!formData.equipment_rental_only && (isAdmin) && (
            <Card>
              {/* The picker sits under the title on a phone, at full width */}
              <FormSectionHeader icon={UserCog} title="Crew labour" className="max-sm:flex-col max-sm:items-stretch">
                <div className="sm:w-1/2">
                  <CrewLabourSelect
                    crewMembers={crewMembers}
                    onSelect={addCrewLabourFromSelect}
                    formatCurrency={formatCurrency}
                  />
                </div>
              </FormSectionHeader>
              <CardContent className="space-y-4 max-sm:px-4 max-sm:pb-4">
                <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleCrewDragEnd}>
                  <SortableContext items={formData.crew_labour.map(c => c.id)} strategy={verticalListSortingStrategy}>
                    {formData.crew_labour.map((crew, index) => (
                      <SortableCrewItem
                        key={crew.id}
                        crew={crew}
                        index={index}
                        onUpdate={updateCrewLabour}
                        onRemove={removeCrewLabour}
                        crewMembers={crewMembers}
                        formatCurrency={formatCurrency}
                        regionConfig={regionConfig}
                      />
                    ))}
                  </SortableContext>
                </DndContext>
                
                {/* Crew Superannuation - Auto-calculated */}
                {crewLabourTotal > 0 && formData.crew_superannuation && (
                  <div className="mt-4 pt-4 border-t">
                    <div className="p-3 rounded-lg bg-mist">
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-medium">Crew Superannuation</span>
                          <span className="text-xs text-muted-foreground">(Auto-calculated)</span>
                        </div>
                        <span className="font-mono text-sm font-medium">{formatCurrency(crewSuperTotal)}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <Label className="text-xs text-muted-foreground">Super Rate:</Label>
                        <Input
                          type="number"
                          step="0.1"
                          value={formData.crew_superannuation.percentage}
                          onChange={(e) => setFormData(prev => ({
                            ...prev,
                            crew_superannuation: {
                              ...prev.crew_superannuation,
                              percentage: parseFloat(e.target.value) || 0,
                            },
                          }))}
                          className="h-8 w-20 no-spinner border-muted-foreground/40"
                        />
                        <span className="text-xs text-muted-foreground">% of crew labour ({formatCurrency(crewLabourTotal)})</span>
                      </div>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
            )}

            <JobEquipmentSection
              formData={formData}
              setFormData={setFormData}
              equipment={equipment}
              equipmentKits={equipmentKits}
              crewMembers={crewMembers}
              isAdmin={isAdmin}
              handlers={handlers}
              sensors={sensors}
              formatCurrency={formatCurrency}
              regionConfig={regionConfig}
              calculatePackageTotal={calculatePackageTotal}
              onEditPackage={openPackageDialog}
            />

            {/* Expenses with Drag & Drop */}
            <Card>
              <FormSectionHeader
                icon={Receipt}
                title="Expenses"
                action={{ label: 'Add Expense', onClick: addExpense, testId: 'add-expense-btn' }}
              />
              <CardContent className="max-sm:px-4 max-sm:pb-4">
                {formData.expenses.length === 0 ? (
                  <p className="text-muted-foreground text-sm text-center py-4">
                    No expenses added. Click &quot;Add Expense&quot; to add costs like travel, accommodation, or materials.
                  </p>
                ) : (
                  <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleExpenseDragEnd}>
                    <SortableContext items={formData.expenses.map(e => e.id)} strategy={verticalListSortingStrategy}>
                      <div className="space-y-2">
                        {formData.expenses.map((expense, index) => (
                          <SortableExpenseItem
                            key={expense.id}
                            expense={expense}
                            index={index}
                            onUpdate={updateExpense}
                            onRemove={removeExpense}
                            formatCurrency={formatCurrency}
                            crewMembers={crewMembers}
                            canLinkCrew={isAdmin}
                          />
                        ))}
                      </div>
                    </SortableContext>
                  </DndContext>
                )}
              </CardContent>
            </Card>

            {/* Hidden Costs with Drag & Drop */}
            <Card>
              <FormSectionHeader
                icon={Receipt}
                iconClassName="text-ochre"
                title="Hidden costs"
                subtitle="Not included in estimates or invoices"
                action={{ label: 'Add Hidden Cost', onClick: addHiddenCost }}
              />
              <CardContent className="max-sm:px-4 max-sm:pb-4">
                {formData.hidden_costs.length === 0 ? (
                  <p className="text-muted-foreground text-sm text-center py-4">
                    No hidden costs added. Use this for internal costs you don&apos;t want to show on invoices.
                  </p>
                ) : (
                  <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleHiddenCostDragEnd}>
                    <SortableContext items={formData.hidden_costs.map(e => e.id)} strategy={verticalListSortingStrategy}>
                      <div className="space-y-2">
                        {formData.hidden_costs.map((cost, index) => (
                          <SortableExpenseItem
                            key={cost.id}
                            expense={cost}
                            index={index}
                            onUpdate={updateHiddenCost}
                            onRemove={removeHiddenCost}
                            formatCurrency={formatCurrency}
                          />
                        ))}
                      </div>
                    </SortableContext>
                  </DndContext>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Sidebar */}
          <div className="lg:col-span-4 space-y-4">
              {/* Client Reference - not sticky */}
              <Card>
                <CardHeader className="pb-3 max-sm:p-4 max-sm:pb-3">
                  <CardTitle className="text-base">Client reference</CardTitle>
                </CardHeader>
                <CardContent className="space-y-3 max-sm:px-4 max-sm:pb-4">
                  <div className="space-y-2">
                    <Label htmlFor="client_job_number" className="text-sm">Client Job Number</Label>
                    <Input
                      id="client_job_number"
                      value={formData.client_job_number}
                      onChange={(e) => setFormData(prev => ({ ...prev, client_job_number: e.target.value }))}
                      placeholder="e.g., PO-12345"
                      data-testid="client-job-number"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="client_job_name" className="text-sm">Client Job Name</Label>
                    <Input
                      id="client_job_name"
                      value={formData.client_job_name}
                      onChange={(e) => setFormData(prev => ({ ...prev, client_job_name: e.target.value }))}
                      placeholder="Client's name for this job"
                      data-testid="client-job-name"
                    />
                  </div>
                </CardContent>
              </Card>

              <div className="sticky top-8 space-y-4">
              {/* Estimate Summary */}
              {/* The estimate: this screen's one inverse band */}
              <Card className="bg-inverse text-inverse-foreground rounded-highlight">
                <CardHeader className="max-sm:p-4">
                  <CardTitle>{isCreate ? 'Estimate summary' : 'Updated estimate'}</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4 max-sm:px-4 max-sm:pb-4">
                  <div className="space-y-2">
                    {!formData.equipment_rental_only && (
                    <>
                    <div className="flex justify-between text-sm">
                      <span className="text-inverse-muted">Labour Total</span>
                      <span className="font-mono font-medium">{formatCurrency(totalLabour)}</span>
                    </div>
                    {crewLabourTotal > 0 && (
                      <>
                        <div className="flex justify-between text-xs pl-4">
                          <span className="text-inverse-muted">Your Labour</span>
                          <span className="font-mono">{formatCurrency(labourTotal)}</span>
                        </div>
                        <div className="flex justify-between text-xs pl-4">
                          <span className="text-inverse-muted">Crew Labour</span>
                          <span className="font-mono">{formatCurrency(crewLabourTotal)}</span>
                        </div>
                      </>
                    )}
                    </>
                    )}
                  </div>
                  <div className="space-y-2">
                    <div className="flex justify-between text-sm">
                      <span className="text-inverse-muted">Equipment Total</span>
                      <span className="font-mono font-medium">{formatCurrency(totalEquipment)}</span>
                    </div>
                    {crewEquipmentTotal > 0 && (
                      <>
                        <div className="flex justify-between text-xs pl-4">
                          <span className="text-inverse-muted">Your Equipment</span>
                          <span className="font-mono">{formatCurrency(equipmentSubtotal - packageDiscountTotal)}</span>
                        </div>
                        <div className="flex justify-between text-xs pl-4">
                          <span className="text-inverse-muted">Crew Equipment</span>
                          <span className="font-mono">{formatCurrency(crewEquipmentTotal)}</span>
                        </div>
                      </>
                    )}
                    {/* Without this the equipment total silently reads low. The
                        create page used to subtract the discount and not say so. */}
                    {packageDiscountTotal > 0 && (
                      <div className="flex justify-between text-sm">
                        <span>Package discounts</span>
                        <span className="font-mono font-medium">-{formatCurrency(packageDiscountTotal)}</span>
                      </div>
                    )}
                  </div>
                  {(expensesTotal > 0 || crewSuperTotal > 0) && (
                    <div className="space-y-2">
                      <div className="flex justify-between text-sm">
                        <span className="text-inverse-muted">Expenses Total</span>
                        <span className="font-mono font-medium">{formatCurrency(totalExpenses)}</span>
                      </div>
                      {crewSuperTotal > 0 && (
                        <>
                          {expensesTotal > 0 && (
                            <div className="flex justify-between text-xs pl-4">
                              <span className="text-inverse-muted">General Expenses</span>
                              <span className="font-mono">{formatCurrency(expensesTotal)}</span>
                            </div>
                          )}
                          <div className="flex justify-between text-xs pl-4">
                            <span className="text-inverse-muted">Crew Superannuation</span>
                            <span className="font-mono">{formatCurrency(crewSuperTotal)}</span>
                          </div>
                        </>
                      )}
                    </div>
                  )}
                  <div className="border-t border-inverse-muted/30 pt-3">
                    <div className="flex justify-between text-sm">
                      <span className="text-inverse-muted">Subtotal</span>
                      <span className="font-mono">{formatCurrency(subtotal)}</span>
                    </div>
                  </div>
                  
                  {/* Tax Toggle & Amount */}
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Switch 
                        id="gst-toggle"
                        checked={formData.include_gst}
                        onCheckedChange={(checked) => setFormData(prev => ({ ...prev, include_gst: checked }))}
                        className="data-[state=checked]:bg-inverse-foreground data-[state=unchecked]:bg-inverse-muted/40 [&>span]:bg-inverse"
                        data-testid="gst-toggle"
                      />
                      <Label htmlFor="gst-toggle" className="text-sm text-inverse-muted">
                        Include {getTaxName()} ({formData.gst_rate}%)
                      </Label>
                    </div>
                    {formData.include_gst && (
                      <span className="font-mono text-sm">{formatCurrency(gstAmount)}</span>
                    )}
                  </div>
                  
                  <div className="border-t border-inverse-muted/30 pt-4">
                    <div className="flex flex-col gap-1">
                      <span className="eyebrow text-inverse-muted">grand total</span>
                      <span className="text-5xl font-light tracking-tight leading-none" data-testid="grand-total">
                        {formatCurrency(grandTotal)}
                      </span>
                    </div>
                    {formData.include_gst && (
                      <p className="text-sm text-inverse-muted mt-1">Inc. {getTaxName()}</p>
                    )}
                  </div>
                </CardContent>
              </Card>

              <Button
                type="submit"
                className="w-full"
                size="lg"
                disabled={loading || (isCreate && !formData.client_id)}
                data-testid={isCreate ? 'create-job-submit' : 'save-job-btn'}
              >
                {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                {isCreate ? 'Create job' : 'Save changes'}
              </Button>
              {isCreate && !formData.client_id && (
                <p className="text-xs text-muted-foreground text-center">Please select a client to continue</p>
              )}
              {onCancel && (
                <Button type="button" variant="outline" className="w-full" onClick={onCancel}>
                  Cancel
                </Button>
              )}
            </div>
          </div>
        </form>
      </div>

      {/* Equipment Package Dialog */}
      <Dialog open={showPackageDialog} onOpenChange={setShowPackageDialog}>
        <DialogContent className="w-[calc(100vw-2rem)] max-w-lg max-h-[85vh] sm:max-h-[80vh] overflow-hidden flex flex-col">
          <DialogHeader className="flex-shrink-0">
            <DialogTitle>{editingPackage ? 'Edit Package' : 'Create Package'}</DialogTitle>
          </DialogHeader>
          <div className="flex flex-col flex-1 overflow-hidden space-y-4">
            <div className="space-y-2 flex-shrink-0">
              <Label htmlFor="package-name">Package Name</Label>
              <Input
                id="package-name"
                value={packageForm.name}
                onChange={(e) => setPackageForm({ ...packageForm, name: e.target.value })}
                placeholder="e.g., Camera Package"
              />
            </div>
            
            <div className="space-y-2 flex-1 flex flex-col overflow-hidden">
              <Label className="flex-shrink-0">Equipment Items</Label>
              <p className="text-sm text-muted-foreground flex-shrink-0">Select equipment to include in this package</p>
              
              {formData.equipment.length === 0 ? (
                <div className="text-center py-4 text-muted-foreground">
                  No equipment available. Add equipment first.
                </div>
              ) : (
                <div className="border rounded-md flex-1 overflow-y-auto min-h-0">
                  {formData.equipment.map((item) => {
                    const isInOtherPackage = formData.equipment_packages.some(
                      pkg => pkg.id !== editingPackage?.id && pkg.equipment_ids.includes(item.inventory_id)
                    );
                    const isSelected = packageForm.equipment_ids.includes(item.inventory_id);
                    
                    return (
                      <div
                        key={item.id}
                        className={cn(
                          "flex items-center gap-2 sm:gap-3 p-2 sm:p-3 border-b last:border-b-0 hover:bg-muted/50 cursor-pointer",
                          isSelected ? 'bg-primary/5' : '',
                          isInOtherPackage && "opacity-50 cursor-not-allowed"
                        )}
                        onClick={() => {
                          if (isInOtherPackage) return;
                          if (isSelected) {
                            setPackageForm({
                              ...packageForm,
                              equipment_ids: packageForm.equipment_ids.filter(id => id !== item.inventory_id),
                            });
                          } else {
                            setPackageForm({
                              ...packageForm,
                              equipment_ids: [...packageForm.equipment_ids, item.inventory_id],
                            });
                          }
                        }}
                      >
                        <div 
                          className={cn(
                            "w-5 h-5 flex-shrink-0 rounded border-2 flex items-center justify-center",
                            isSelected 
                              ? 'bg-primary border-primary' 
                              : 'border-muted-foreground/30'
                          )}
                        >
                          {isSelected && (
                            <svg className="w-3 h-3 text-primary-foreground" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                              <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                            </svg>
                          )}
                        </div>
                        <div className="flex-1 min-w-0 overflow-hidden">
                          <div className="font-medium truncate text-sm sm:text-base" title={item.name}>{item.name}</div>
                          <div className="text-xs sm:text-sm text-muted-foreground truncate">
                            {formatCurrency((item.rate_override || item.daily_rate) * item.scheduled_days * item.quantity)}
                            {isInOtherPackage && ' • In another package'}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {packageForm.equipment_ids.length > 0 && (
              <div className="bg-muted/50 rounded-md p-3 flex-shrink-0">
                <div className="text-sm font-medium">
                  {packageForm.equipment_ids.length} item(s) selected
                </div>
              </div>
            )}
            
            <div className="grid grid-cols-2 gap-4 flex-shrink-0">
              <div className="space-y-2">
                <Label>Discount Type</Label>
                <Select
                  value={packageForm.discount_type}
                  onValueChange={(value) => setPackageForm({ ...packageForm, discount_type: value, discount_value: 0 })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="percentage">Percentage (%)</SelectItem>
                    <SelectItem value="fixed">Fixed Discount ($)</SelectItem>
                    <SelectItem value="budget">Fixed Budget ($)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="discount-value">
                  {packageForm.discount_type === 'percentage' ? 'Discount %' : 
                   packageForm.discount_type === 'budget' ? 'Budget Amount' : 'Discount Amount'}
                </Label>
                <div className="relative">
                  {(packageForm.discount_type === 'fixed' || packageForm.discount_type === 'budget') && (
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">$</span>
                  )}
                  <Input
                    id="discount-value"
                    type="number"
                    step={packageForm.discount_type === 'percentage' ? '1' : '0.01'}
                    min="0"
                    max={packageForm.discount_type === 'percentage' ? '100' : undefined}
                    value={packageForm.discount_value}
                    onChange={(e) => setPackageForm({ ...packageForm, discount_value: e.target.value })}
                    placeholder={packageForm.discount_type === 'percentage' ? '0' : '0'}
                    className={cn("no-spinner", (packageForm.discount_type === 'fixed' || packageForm.discount_type === 'budget') && "pl-7")}
                  />
                  {packageForm.discount_type === 'percentage' && (
                    <span className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground">%</span>
                  )}
                </div>
              </div>
            </div>

            <Button onClick={handleSavePackage} className="w-full flex-shrink-0" disabled={packageForm.equipment_ids.length === 0}>
              {editingPackage ? 'Save Changes' : 'Create Package'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {children}
    </>
  );
}
